import { useEffect, useState } from "react"

import { advisorsList } from "@/features/advisors"
import { ReportPromoSlider } from "@/features/today"

import { useChat } from "../hooks/use-chat"
import { formatThreadDate } from "../lib/format-thread-date"
import { recencyTimestamp, threadRecencySeconds } from "../lib/thread-recency"
import { ASSISTANT_STATUSES } from "../types"
import { TypingIndicator } from "./typing-indicator"

/** Readings tab default view: a strip to start new chats + the list of conversations. */
export function ChatsList() {
  const {
    chatsMessages,
    unreadAdvisorIds,
    assistantStatuses,
    threads,
    activityAtByAssistant,
    setSelectedAdvisor,
    updateThreads,
    userMainSubscriptionId,
  } = useChat()
  const threadByAssistant = new Map(threads.map((t) => [t.assistantId, t]))
  const unreadCountByAdvisor = unreadAdvisorIds.reduce<Record<string, number>>((acc, id) => {
    acc[id] = (acc[id] ?? 0) + 1
    return acc
  }, {})
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    updateThreads()
    const t = setTimeout(() => setIsLoading(false), 700)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const startChat = (assistantId: string) => {
    const advisor = advisorsList.find((a) => a.assistantId === assistantId)
    if (advisor) setSelectedAdvisor(advisor)
  }

  const showLoader = (!!threads.length && isLoading) || chatsMessages.length === 0
  const recencyOf = (assistantId: string) =>
    threadRecencySeconds(threadByAssistant.get(assistantId), activityAtByAssistant[assistantId])
  const orderedChats = [...chatsMessages].sort(
    (a, b) => recencyOf(b.assistantId) - recencyOf(a.assistantId),
  )

  return (
    <div className="flex flex-col">
      {/* Start a new chat */}
      <div className="no-scrollbar -mx-4 flex gap-4 overflow-x-auto px-4 pb-4">
        {advisorsList.map((advisor) => (
          <button
            key={advisor.assistantId}
            type="button"
            onClick={() => startChat(advisor.assistantId)}
            className="flex shrink-0 flex-col items-center gap-1"
          >
            <span className="flex size-16 items-center justify-center rounded-full bg-gradient-to-br from-peach via-peach to-purple-accent p-0.5">
              <img
                src={advisor.photo}
                alt={advisor.name}
                className="size-full rounded-full border-2 border-dark-base object-cover object-top"
              />
            </span>
            <span className="max-w-16 truncate font-body text-xs text-purple-light/70">
              {advisor.name.split(" ")[0]}
            </span>
          </button>
        ))}
      </div>

      <ReportPromoSlider userMainSubscriptionId={userMainSubscriptionId} className="mb-4" />

      {showLoader ? (
        <div className="flex justify-center py-10">
          <span className="size-6 animate-spin rounded-full border-2 border-purple-light/40 border-t-transparent" />
        </div>
      ) : (
        <div className="flex flex-col">
          {orderedChats.map((chat, i) => {
            const advisor = advisorsList.find((a) => a.assistantId === chat.assistantId)
            if (!advisor) return null
            const unreadCount = unreadCountByAdvisor[chat.assistantId] ?? 0
            const status = assistantStatuses.find((s) => s.assistantId === chat.assistantId)?.status
            const recency = recencyOf(chat.assistantId)
            const dateLabel = formatThreadDate(recencyTimestamp(recency))

            return (
              <div key={chat.assistantId || i}>
                <button
                  type="button"
                  onClick={() => startChat(chat.assistantId)}
                  className="flex w-full items-center text-left"
                >
                  <span className="relative flex size-20 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-peach via-peach to-purple-accent">
                    <img
                      src={advisor.photo}
                      alt={advisor.name}
                      className="size-[74px] rounded-full border-4 border-dark-base object-cover"
                    />
                    <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-xl border-4 border-dark-base bg-dark-base">
                      <span className="flex items-center gap-1 rounded-xl bg-green/20 px-2 py-1 font-body text-[10px] text-green">
                        <span className="size-1.5 rounded-full bg-green" />
                        Online
                      </span>
                    </span>
                  </span>

                  <span className="ml-4 w-full">
                    <span className="mb-1 flex items-center justify-between gap-2">
                      <span className="font-display text-base font-medium text-purple-light">
                        {advisor.name}
                      </span>
                      {dateLabel && (
                        <span className="shrink-0 whitespace-nowrap rounded-full bg-purple-light/10 px-2 py-1 font-body text-[10px] uppercase text-purple-light/70">
                          {dateLabel}
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-4">
                      <span className="line-clamp-2 w-full font-body text-base text-purple-light/70">
                        {status === ASSISTANT_STATUSES.PROCESSING || !chat.message ? (
                          <TypingIndicator />
                        ) : (
                          chat.message
                        )}
                      </span>
                      {unreadCount > 0 && (
                        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-sm font-medium text-white">
                          {unreadCount}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
                <div className="ml-24 my-4 h-px bg-purple-light/10" />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
